import { api } from "@/app/services/api.service";

export const URSB_KEYS_PATH = "configuration/integration/obrs-third-party-keys";
export const URSB_KEYS_AUDIT_PATH = "configuration/integration/obrs-third-party-keys/audit";

export type UrsbIntegrationKeys = {
  apiKey: string | null;
  secretKey: string | null;
  configured: boolean;
};

export function unwrapPayload(res: { data: unknown }) {
  const data = res.data as Record<string, unknown>;
  if (
    data != null &&
    typeof data === "object" &&
    Object.prototype.hasOwnProperty.call(data, "data") &&
    data.data != null
  ) {
    return data.data;
  }
  return data;
}

export function extractErrorMessage(error: unknown, fallback: string) {
  const err = error as { response?: { data?: { message?: string } } };
  return err.response?.data?.message || fallback;
}

function readString(record: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = record[key];
    if (value != null && value !== "") return String(value);
  }
  return null;
}

export function parseUrsbIntegrationKeys(body: unknown): UrsbIntegrationKeys {
  if (body == null || typeof body !== "object") {
    return { apiKey: null, secretKey: null, configured: false };
  }
  const record = body as Record<string, unknown>;
  const apiKey = readString(record, ["apiKey", "api_key", "key"]);
  const secretKey = readString(record, ["secretKey", "secret_key", "secret"]);
  const configured =
    record.configured === true ||
    record.hasKeys === true ||
    (apiKey != null && apiKey.length > 0);

  return {
    apiKey,
    secretKey,
    configured,
  };
}

export async function fetchUrsbIntegrationKeys(): Promise<UrsbIntegrationKeys> {
  const res = await api().get(URSB_KEYS_PATH);
  return parseUrsbIntegrationKeys(unwrapPayload(res));
}

export async function saveUrsbIntegrationKeys(payload: {
  apiKey: string;
  secretKey: string;
}) {
  await api().post(URSB_KEYS_PATH, payload);
}

export async function deleteUrsbIntegrationKeys() {
  await api().delete(URSB_KEYS_PATH);
}

export function extractAuditList(body: unknown): Record<string, unknown>[] {
  if (body == null) return [];
  if (Array.isArray(body)) {
    return body.filter((item) => item != null && typeof item === "object") as Record<string, unknown>[];
  }
  const obj = body as Record<string, unknown>;
  const candidates = [obj.auditLogs, obj.logs, obj.items, obj.content, obj.records, obj.data];
  for (const candidate of candidates) {
    const parsed = extractAuditList(candidate);
    if (parsed.length > 0) return parsed;
  }
  return [];
}

export async function fetchUrsbKeysAudit(): Promise<Record<string, unknown>[]> {
  const res = await api().get(URSB_KEYS_AUDIT_PATH);
  const root = res.data;
  if (Array.isArray(root)) return extractAuditList(root);
  if (root != null && typeof root === "object") {
    const rootObj = root as Record<string, unknown>;
    if (Array.isArray(rootObj.data)) return extractAuditList(rootObj.data);
    const fromUnwrapped = extractAuditList(unwrapPayload(res));
    if (fromUnwrapped.length > 0) return fromUnwrapped;
    return extractAuditList(root);
  }
  return [];
}
